import { useState, useEffect, useCallback } from "react";
import { ShoppingBag, Calendar, Star, Trash2, Phone, Mail, ArrowLeft, FileText, Download, Edit2, Plus, X, Save, LogOut, Tag, DollarSign, Lock, Upload, CheckCircle, Image, ChevronUp, ChevronDown } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config/constants";
import { useAuth } from "../../hooks/useAuth";
import { useSiteImages } from "../../hooks/useSiteImages";
import { SITE_IMAGE_SLOTS, SITE_EMBED_SLOTS } from "../../config/siteImages";
import { extractEmbedSrc } from "../../components/GlobalETicketsWidget";

function SiteEmbedRow({ slot, currentUrl, onSave }) {
  const [url, setUrl] = useState(currentUrl);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setUrl(currentUrl); }, [currentUrl]);

  const handleSave = async () => { setSaving(true); try { await onSave(slot.key, url); toast.success("Saved successfully"); } catch { toast.error("Save failed"); } finally { setSaving(false); } };
  const handleReset = async () => { setSaving(true); try { await onSave(slot.key, ""); toast.success("Reset to default"); } catch { toast.error("Reset failed"); } finally { setSaving(false); } };

  return (
    <div className="p-4 hover:bg-gray-50">
      <div className="font-medium text-sm text-[#0F2137] mb-1">{slot.label}</div>
      {slot.help && <p className="text-xs text-[#495057] mb-2">{slot.help}</p>}
      <div className="flex flex-col sm:flex-row gap-2">
        <input value={url} onChange={e => setUrl(e.target.value)} className="flex-1 px-3 py-2 border rounded-lg text-sm" placeholder="https://globaletickets.com/e/..." />
        <div className="flex items-center gap-2">
          <button onClick={handleSave} disabled={saving} className="px-3 py-2 text-sm bg-[#D90429] text-white rounded-lg hover:bg-[#B00322] disabled:opacity-50">Save</button>
          <button onClick={handleReset} disabled={saving} className="px-3 py-2 text-sm bg-gray-200 text-[#0F2137] rounded-lg hover:bg-gray-300 disabled:opacity-50">Reset</button>
        </div>
      </div>
      {url && !extractEmbedSrc(url) && (
        <p className="mt-2 text-xs text-red-600">
          That doesn't look like a link. Paste the address starting with https:// (or paste the whole &lt;iframe&gt; code and the link will be pulled out of it).
        </p>
      )}
      {extractEmbedSrc(url) && (
        <div className="mt-3">
          <p className="text-xs text-[#495057] mb-1">Preview:</p>
          <iframe src={extractEmbedSrc(url)} title="Embed preview" width="100%" height="320" frameBorder="0" style={{ borderRadius: "12px", maxWidth: "400px", border: "1px solid #e5e7eb" }} />
        </div>
      )}
    </div>
  );
}

function parsePos(value) {
  const m = (value || "").match(/(\d+)%/);
  return m ? Number(m[1]) : 50;
}

function SiteImageRow({ slot, currentUrl, onSave, positionValue, onSavePosition }) {
  const [url, setUrl] = useState(currentUrl);
  const [pos, setPos] = useState(parsePos(positionValue));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setUrl(currentUrl); }, [currentUrl]);
  useEffect(() => { setPos(parsePos(positionValue)); }, [positionValue]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) { toast.error("Only JPEG and PNG images accepted."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/upload`, fd);
      const newUrl = `${API}/files/${res.data.id}`;
      setUrl(newUrl);
      await onSave(slot.key, newUrl);
      toast.success("Image uploaded and saved");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleSave = async () => { setSaving(true); try { await onSave(slot.key, url); toast.success("Saved successfully"); } catch { toast.error("Save failed"); } finally { setSaving(false); } };
  const handleReset = async () => { setSaving(true); try { await onSave(slot.key, ""); toast.success("Reset to default"); } catch { toast.error("Reset failed"); } finally { setSaving(false); } };
  const savePosition = async () => { try { await onSavePosition(slot.key, `center ${pos}%`); toast.success("Position saved"); } catch { toast.error("Could not save position"); } };

  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:bg-gray-50">
      {slot.circle
        ? <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex-shrink-0 border"><img src={url} alt={slot.label} className="w-full h-full object-cover" style={{ objectPosition: `center ${pos}%` }} onError={e => { e.target.style.opacity = "0.2"; }} /></div>
        : <img src={url} alt={slot.label} className="w-20 h-20 rounded-lg object-contain bg-gray-100 flex-shrink-0 border" onError={e => { e.target.style.opacity = "0.2"; }} />}
      <div className="flex-1 min-w-0">
        <div className="font-medium text-sm text-[#0F2137] mb-1">{slot.label}</div>
        <input value={url} onChange={e => setUrl(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Image URL or upload a file" />
        {slot.circle && (
          <div className="mt-2">
            <label className="block text-xs text-[#495057] mb-1">Position in circle (drag to move the medal up/down, then click Save Position)</label>
            <div className="flex items-center gap-3">
              <span className="text-xs text-[#495057]">Top</span>
              <input type="range" min="0" max="100" value={pos} onChange={e => setPos(Number(e.target.value))} className="flex-1" />
              <span className="text-xs text-[#495057]">Bottom</span>
              <button onClick={savePosition} className="px-3 py-1.5 text-xs bg-[#0F2137] text-white rounded-lg hover:bg-[#1a3a5c] whitespace-nowrap">Save Position</button>
            </div>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <label className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 whitespace-nowrap">
          <input type="file" accept="image/jpeg,image/png" onChange={handleUpload} className="hidden" />
          <Upload size={16} /> {uploading ? "Uploading..." : "Upload"}
        </label>
        <button onClick={handleSave} disabled={saving || uploading} className="px-3 py-2 text-sm bg-[#D90429] text-white rounded-lg hover:bg-[#B00322] disabled:opacity-50">Save</button>
        <button onClick={handleReset} disabled={saving || uploading} className="px-3 py-2 text-sm bg-gray-200 text-[#0F2137] rounded-lg hover:bg-gray-300 disabled:opacity-50">Reset</button>
      </div>
    </div>
  );
}

function uploadVideoFile(file) {
  if (!file) return Promise.reject(new Error("no file"));
  if (!["video/mp4", "video/webm", "video/quicktime"].includes(file.type)) { toast.error("Only MP4, WebM, or MOV videos accepted."); return Promise.reject(new Error("bad type")); }
  if (file.size > 60 * 1024 * 1024) { toast.error("Video too large (max 60MB)."); return Promise.reject(new Error("too large")); }
  const fd = new FormData();
  fd.append("file", file);
  return axios.post(`${API}/upload`, fd).then((res) => `${API}/files/${res.data.id}`);
}

function MedalVideoRow({ video, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [title, setTitle] = useState(video.title || "");
  const [url, setUrl] = useState(video.url || "");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setTitle(video.title || ""); setUrl(video.url || ""); }, [video]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const newUrl = await uploadVideoFile(file);
      setUrl(newUrl);
      await onUpdate(video.id, { url: newUrl });
      toast.success("Video uploaded and saved");
    } catch (err) { if (!["bad type", "too large"].includes(err.message)) toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleSave = async () => { setSaving(true); try { await onUpdate(video.id, { title, url }); toast.success("Saved successfully"); } catch { toast.error("Save failed"); } finally { setSaving(false); } };

  return (
    <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4 hover:bg-gray-50">
      <div className="flex flex-col gap-1 flex-shrink-0">
        <button onClick={onMoveUp} disabled={isFirst} title="Move up" className="p-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronUp size={16} /></button>
        <button onClick={onMoveDown} disabled={isLast} title="Move down" className="p-1 rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"><ChevronDown size={16} /></button>
      </div>
      <video src={url} className="w-28 h-16 rounded-lg object-cover flex-shrink-0 bg-black" muted playsInline preload="metadata" />
      <div className="flex-1 min-w-0 space-y-2">
        <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Video title" />
        <input value={url} onChange={e => setUrl(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Video URL or upload a file" />
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <label className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 whitespace-nowrap">
          <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleUpload} className="hidden" />
          <Upload size={16} /> {uploading ? "Uploading..." : "Upload"}
        </label>
        <button onClick={handleSave} disabled={saving || uploading} className="px-3 py-2 text-sm bg-[#D90429] text-white rounded-lg hover:bg-[#B00322] disabled:opacity-50">Save</button>
        <button onClick={() => onDelete(video.id)} className="text-red-500 hover:text-red-700 p-2"><Trash2 size={16} /></button>
      </div>
    </div>
  );
}

function AddMedalVideo({ onAdd }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [adding, setAdding] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const newUrl = await uploadVideoFile(file);
      setUrl(newUrl);
      toast.success("Video uploaded — click Add Video to publish");
    } catch (err) { if (!["bad type", "too large"].includes(err.message)) toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const handleAdd = async () => {
    if (!url) { toast.error("Upload a video or paste a URL first."); return; }
    setAdding(true);
    try { await onAdd(title, url); setTitle(""); setUrl(""); toast.success("Video added"); }
    catch { toast.error("Failed to add video"); }
    finally { setAdding(false); }
  };

  return (
    <div className="p-4 bg-gray-50 flex flex-col sm:flex-row sm:items-center gap-4 border-t-2 border-dashed">
      {url
        ? <video src={url} className="w-28 h-16 rounded-lg object-cover flex-shrink-0 bg-black" muted playsInline preload="metadata" />
        : <div className="w-28 h-16 rounded-lg bg-gray-200 flex items-center justify-center text-gray-400 flex-shrink-0"><Plus size={20} /></div>}
      <div className="flex-1 min-w-0 space-y-2">
        <input value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="New video title" />
        <input value={url} onChange={e => setUrl(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Upload a file or paste a video URL" />
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <label className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 whitespace-nowrap">
          <input type="file" accept="video/mp4,video/webm,video/quicktime" onChange={handleUpload} className="hidden" />
          <Upload size={16} /> {uploading ? "Uploading..." : "Upload"}
        </label>
        <button onClick={handleAdd} disabled={adding || uploading} className="px-3 py-2 text-sm bg-[#0F2137] text-white rounded-lg hover:bg-[#1a3a5c] disabled:opacity-50 inline-flex items-center gap-1"><Plus size={16} /> Add Video</button>
      </div>
    </div>
  );
}

const tabs = [
  { id: "merch", label: "Merch Products", icon: <Tag size={18} /> },
  { id: "events", label: "Events", icon: <Calendar size={18} /> },
  { id: "transactions", label: "Transactions", icon: <DollarSign size={18} /> },
  { id: "reviews", label: "Reviews", icon: <Star size={18} /> },
  { id: "contacts", label: "Contacts", icon: <Mail size={18} /> },
  { id: "callbacks", label: "Callbacks", icon: <Phone size={18} /> },
  { id: "uploads", label: "Uploads", icon: <FileText size={18} /> },
];

function ChangePasswordModal({ onClose, authHeader }) {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSave = async () => {
    setError("");
    if (newPw.length < 6) { setError("New password must be at least 6 characters."); return; }
    if (newPw !== confirm) { setError("Passwords do not match."); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: newPw }, { headers: authHeader() });
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to change password.");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-md w-full p-6" onClick={e => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0F2137] flex items-center gap-2"><Lock size={20} /> Change Password</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {success ? (
          <div className="flex items-center gap-2 text-green-600 py-4"><CheckCircle size={20} /> Password changed successfully!</div>
        ) : (
          <div className="space-y-4">
            {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg" data-testid="password-error">{error}</p>}
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Current Password</label>
              <input type="password" value={current} onChange={e => setCurrent(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="current-password" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">New Password</label>
              <input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="new-password" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Confirm New Password</label>
              <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} className="w-full px-3 py-2 border rounded-lg" data-testid="confirm-password" />
            </div>
            <div className="flex justify-end gap-3 mt-2">
              <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-[#D90429] text-white rounded-lg hover:bg-[#B00322] disabled:opacity-50" data-testid="save-password-btn">
                {saving ? "Saving..." : "Change Password"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function EditMerchModal({ product, onClose, onSave }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (product) {
      setForm({ price: product.price || 0, shipping: product.shipping || 0, description: product.description || "", in_stock: product.in_stock !== false });
    }
  }, [product]);

  if (!product) return null;

  const handleSave = async () => {
    setSaving(true);
    try { await onSave(product.id, form); onClose(); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="edit-merch-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0F2137]">Edit: {product.name}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        {product.type !== "tent" ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Price ($)</label>
              <input type="number" step="0.01" value={form.price} onChange={e => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-price" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Shipping ($)</label>
              <input type="number" step="0.01" value={form.shipping} onChange={e => setForm({ ...form, shipping: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-shipping" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 border rounded-lg" data-testid="edit-description" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.in_stock} onChange={e => setForm({ ...form, in_stock: e.target.checked })} className="w-4 h-4" id="in-stock" data-testid="edit-in-stock" />
              <label htmlFor="in-stock" className="text-sm text-[#0F2137]">In Stock</label>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Flat Shipping ($)</label>
              <input type="number" step="0.01" value={form.shipping} onChange={e => setForm({ ...form, shipping: parseFloat(e.target.value) || 0 })} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 border rounded-lg" />
            </div>
            <div className="flex items-center gap-2">
              <input type="checkbox" checked={form.in_stock} onChange={e => setForm({ ...form, in_stock: e.target.checked })} className="w-4 h-4" id="tent-stock" />
              <label htmlFor="tent-stock" className="text-sm text-[#0F2137]">In Stock</label>
            </div>
            <p className="text-xs text-[#495057]">Edit tent pricing via the inline pricing editor in the merch table.</p>
          </div>
        )}
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-[#D90429] text-white rounded-lg hover:bg-[#B00322] disabled:opacity-50 flex items-center gap-1" data-testid="save-merch-btn">
            <Save size={14} />{saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TentPricingEditor({ product, onSave }) {
  const [pricing, setPricing] = useState(product.tentPricing || {});
  const [saving, setSaving] = useState(false);
  const handlePriceChange = (config, size, value) => {
    setPricing(prev => ({ ...prev, [config]: { ...prev[config], [size]: parseFloat(value) || 0 } }));
  };
  const handleSave = async () => {
    setSaving(true);
    try { await onSave(product.id, { tentPricing: pricing }); } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };
  const sizes = product.tentSizes || [];
  const configs = product.tentConfigs || [];
  return (
    <div className="mt-4 bg-gray-50 rounded-lg p-4" data-testid="tent-pricing-editor">
      <h4 className="text-sm font-semibold text-[#0F2137] mb-3">Tent Pricing Matrix</h4>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left"><th className="py-1 pr-2 font-medium text-[#495057]">Configuration</th>{sizes.map(s => <th key={s} className="py-1 px-2 font-medium text-[#495057] text-center">{s}</th>)}</tr></thead>
          <tbody>{configs.map(config => (<tr key={config} className="border-t"><td className="py-2 pr-2 text-[#0F2137] text-xs">{config}</td>{sizes.map(size => (<td key={size} className="py-2 px-1"><input type="number" step="1" value={pricing[config]?.[size] || 0} onChange={e => handlePriceChange(config, size, e.target.value)} className="w-20 px-2 py-1 border rounded text-xs text-center" /></td>))}</tr>))}</tbody>
        </table>
      </div>
      <button onClick={handleSave} disabled={saving} className="mt-3 px-3 py-1.5 text-xs bg-[#D90429] text-white rounded hover:bg-[#B00322] disabled:opacity-50 flex items-center gap-1" data-testid="save-tent-pricing"><Save size={12} />{saving ? "Saving..." : "Save Pricing"}</button>
    </div>
  );
}

function EventFormModal({ event, onClose, onSave }) {
  const isNew = !event;
  const [form, setForm] = useState({
    name: "", host: "", date: "", location: "", contact: "", image_url: "", register_url: "", featured: false,
    ...(event || {}),
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleSave = async () => {
    if (!form.name || !form.date || !form.location) { toast.error("Name, date, and location are required."); return; }
    setSaving(true);
    try { await onSave(form, isNew); onClose(); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ["image/jpeg", "image/png"];
    if (!allowed.includes(file.type)) { toast.error("Only JPEG and PNG images accepted."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("File too large (max 10MB)."); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData);
      const imageUrl = `${API}/files/${res.data.id}`;
      setForm(prev => ({ ...prev, image_url: imageUrl }));
    } catch { toast.error("Image upload failed."); }
    finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="event-form-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#0F2137]">{isNew ? "Add Event" : "Edit Event"}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[#0F2137] mb-1">Event Name *</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="event-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F2137] mb-1">Host</label>
            <input value={form.host} onChange={e => setForm({ ...form, host: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="e.g. Hosted by..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Date *</label>
              <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="event-date-input" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F2137] mb-1">Location *</label>
              <input value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} className="w-full px-3 py-2 border rounded-lg" data-testid="event-location-input" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F2137] mb-1">Contact</label>
            <input value={form.contact} onChange={e => setForm({ ...form, contact: e.target.value })} className="w-full px-3 py-2 border rounded-lg" placeholder="Name or email" />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F2137] mb-1">Event Image</label>
            <div className="flex items-center gap-3">
              <input value={form.image_url} onChange={e => setForm({ ...form, image_url: e.target.value })} className="flex-1 px-3 py-2 border rounded-lg text-sm" placeholder="Paste URL or upload" />
              <label className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 rounded-lg cursor-pointer hover:bg-gray-200 transition-colors whitespace-nowrap">
                <input type="file" accept="image/jpeg,image/png" onChange={handleImageUpload} className="hidden" data-testid="event-image-upload" />
                {uploading ? <div className="animate-spin w-4 h-4 border-2 border-[#D90429] border-t-transparent rounded-full" /> : <Upload size={14} />}
                {uploading ? "Uploading..." : "Upload"}
              </label>
            </div>
            {form.image_url && (
              <div className="mt-2 relative">
                <img src={form.image_url} alt="Preview" className="w-full h-32 object-cover rounded-lg" onError={e => e.target.style.display = 'none'} />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F2137] mb-1">Registration URL</label>
            <input value={form.register_url} onChange={e => setForm({ ...form, register_url: e.target.value })} className="w-full px-3 py-2 border rounded-lg" />
          </div>
          <div className="flex items-center gap-2">
            <input type="checkbox" checked={form.featured} onChange={e => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4" id="event-featured" />
            <label htmlFor="event-featured" className="text-sm text-[#0F2137]">Featured on homepage</label>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-[#D90429] text-white rounded-lg hover:bg-[#B00322] disabled:opacity-50 flex items-center gap-1" data-testid="save-event-btn">
            <Save size={14} />{saving ? "Saving..." : isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout, authHeader, tryRefresh } = useAuth();
  const { getImage: getSiteImage, refresh: refreshSiteImages } = useSiteImages();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("merch");
  const [data, setData] = useState({ merch: [], events: [], transactions: [], reviews: [], contacts: [], callbacks: [], uploads: [], medal_videos: [] });
  const [loading, setLoading] = useState(true);
  const [editingMerch, setEditingMerch] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [expandedTent, setExpandedTent] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const ax = useCallback(() => {
    const instance = axios.create({ headers: authHeader() });
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && original && !original._retried) {
          original._retried = true;
          const refreshed = await tryRefresh();
          if (refreshed) {
            original.headers = { ...original.headers, ...authHeader() };
            return instance.request(original);
          }
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, [authHeader, tryRefresh]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [merch, events, transactions, reviews, contacts, callbacks, uploads, medalVideos] = await Promise.all([
        ax().get(`${API}/admin/merch-products`).catch(() => ({ data: [] })),
        ax().get(`${API}/events`).catch(() => ({ data: [] })),
        ax().get(`${API}/admin/transactions`).catch(() => ({ data: [] })),
        ax().get(`${API}/reviews`).catch(() => ({ data: [] })),
        ax().get(`${API}/admin/contacts`).catch(() => ({ data: [] })),
        ax().get(`${API}/admin/callbacks`).catch(() => ({ data: [] })),
        ax().get(`${API}/admin/uploads`).catch(() => ({ data: [] })),
        ax().get(`${API}/medal-videos`).catch(() => ({ data: [] })),
      ]);
      setData({ merch: merch.data, events: events.data, transactions: transactions.data, reviews: reviews.data, contacts: contacts.data, callbacks: callbacks.data, uploads: uploads.data, medal_videos: medalVideos.data });
    } catch (error) { console.error("Error fetching data:", error); }
    finally { setLoading(false); }
  }, [ax]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDelete = async (collection, id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;
    try { await ax().delete(`${API}/admin/${collection}/${id}`); fetchData(); toast.success("Deleted"); }
    catch (error) { console.error("Delete error:", error); toast.error("Failed to delete"); }
  };

  const handleToggleFeatured = async (collection, id, currentVal) => {
    try { await ax().patch(`${API}/admin/${collection}/${id}`, { featured: !currentVal }); fetchData(); toast.success("Updated"); }
    catch (error) { console.error("Update error:", error); toast.error("Update failed"); }
  };

  const handleSaveMerch = async (id, updates) => {
    await ax().patch(`${API}/admin/merch-products/${id}`, updates);
    fetchData();
    toast.success("Saved successfully");
  };

  const handleSaveEvent = async (form, isNew) => {
    if (isNew) { await ax().post(`${API}/admin/events`, form); }
    else { const { id, created_at, ...updates } = form; await ax().patch(`${API}/admin/events/${form.id}`, updates); }
    fetchData();
    toast.success(isNew ? "Event created" : "Event saved");
  };

  const handleSaveSiteImage = async (key, url) => {
    await ax().patch(`${API}/admin/site-images/${key}`, { url });
    await refreshSiteImages();
  };

  const handleAddMedalVideo = async (title, url) => {
    await ax().post(`${API}/admin/medal-videos`, { title, url });
    fetchData();
  };

  const handleUpdateMedalVideo = async (id, updates) => {
    await ax().patch(`${API}/admin/medal-videos/${id}`, updates);
    fetchData();
  };

  const handleMoveMedalVideo = async (index, direction) => {
    const list = [...(data.medal_videos || [])];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setData(prev => ({ ...prev, medal_videos: list }));
    try {
      await Promise.all(
        list
          .map((v, i) => (v.sort_order !== i ? ax().patch(`${API}/admin/medal-videos/${v.id}`, { sort_order: i }) : null))
          .filter(Boolean)
      );
      toast.success("Order updated");
    } catch { toast.error("Could not reorder"); }
    fetchData();
  };

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="pt-20 min-h-screen bg-[#F8F9FA]">
      <div className="bg-[#0F2137] py-8">
        <div className="container-custom flex items-center justify-between">
          <div>
            <span className="section-kicker">Admin Panel</span>
            <h1 className="text-3xl font-bold text-white">Dashboard</h1>
            <p className="text-gray-400 text-sm mt-1">Logged in as {user?.email}</p>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm"><ArrowLeft size={18} /> Site</Link>
            <button onClick={() => setShowPasswordModal(true)} data-testid="change-password-btn" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm"><Lock size={18} /> Password</button>
            <button onClick={handleLogout} data-testid="admin-logout" className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors text-sm"><LogOut size={18} /> Logout</button>
          </div>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
          {tabs.map(tab => (
            <div key={tab.id} className="bg-white rounded-xl p-4 shadow-sm text-center cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab(tab.id)}>
              <div className="text-2xl font-bold text-[#D90429]">{data[tab.id]?.length || 0}</div>
              <div className="text-xs text-[#495057] capitalize">{tab.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`admin-tab-${tab.id}`}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === tab.id ? "bg-[#D90429] text-white" : "bg-white text-[#0F2137] hover:bg-gray-100"}`}>
              {tab.icon}{tab.label}
            </button>
          ))}
          <button onClick={() => setActiveTab("site_images")} data-testid="admin-tab-site_images"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${activeTab === "site_images" ? "bg-[#D90429] text-white" : "bg-white text-[#0F2137] hover:bg-gray-100"}`}>
            <Image size={18} />Site Media
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin w-10 h-10 border-4 border-[#D90429] border-t-transparent rounded-full mx-auto" /></div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">

            {/* Merch Tab */}
            {activeTab === "merch" && (
              <div>
                <div className="p-4 bg-gray-50 font-medium text-sm text-[#495057]">Merch Products ({data.merch.length})</div>
                <div className="divide-y">
                  {data.merch.length === 0 ? <p className="p-6 text-center text-[#495057]">No merch products. Run seed to populate.</p> :
                    data.merch.map(product => (
                      <div key={product.id} data-testid={`admin-merch-${product.id}`}>
                        <div className="p-4 flex items-center gap-4 hover:bg-gray-50">
                          {(product.image_url || (product.configImages && Object.values(product.configImages)[0])) && (
                            <img src={product.image_url || Object.values(product.configImages)[0]} alt="" className="w-12 h-12 rounded-lg object-contain bg-gray-100" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-[#0F2137]">{product.name}</p>
                            <p className="text-xs text-[#495057]">{product.type} &middot; {product.category}</p>
                          </div>
                          <div className="text-right">
                            {product.type !== "tent" ? (
                              <p className="text-[#D90429] font-semibold">${product.price?.toFixed(2)}</p>
                            ) : (
                              <button onClick={() => setExpandedTent(!expandedTent)} className="text-xs text-[#D90429] font-semibold hover:underline">
                                {expandedTent ? "Hide Pricing" : "View Pricing"}
                              </button>
                            )}
                          </div>
                          <span className={`text-xs px-2 py-1 rounded-full ${product.in_stock !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {product.in_stock !== false ? 'In Stock' : 'Out of Stock'}
                          </span>
                          <button onClick={() => setEditingMerch(product)} className="text-[#0F2137] hover:text-[#D90429] p-1" data-testid={`edit-merch-${product.id}`}><Edit2 size={16} /></button>
                        </div>
                        {product.type === "tent" && expandedTent && <TentPricingEditor product={product} onSave={handleSaveMerch} />}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Events Tab */}
            {activeTab === "events" && (
              <div>
                <div className="p-4 bg-gray-50 font-medium text-sm text-[#495057] flex items-center justify-between">
                  <span>Events ({data.events.length})</span>
                  <button onClick={() => setShowNewEvent(true)} data-testid="add-event-btn" className="flex items-center gap-1 px-3 py-1.5 text-xs bg-[#D90429] text-white rounded-lg hover:bg-[#B00322]"><Plus size={14} /> Add Event</button>
                </div>
                <div className="divide-y">
                  {data.events.length === 0 ? <p className="p-6 text-center text-[#495057]">No events found.</p> :
                    data.events.map(event => (
                      <div key={event.id} className="p-4 flex items-center gap-4 hover:bg-gray-50 text-sm" data-testid={`admin-event-${event.id}`}>
                        {event.image_url && <img src={event.image_url} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#0F2137]">{event.name}</p>
                          <p className="text-xs text-[#495057]">{event.date} &middot; {event.location}</p>
                        </div>
                        <button onClick={() => handleToggleFeatured('events', event.id, event.featured)} className={`text-xs px-2 py-1 rounded-full ${event.featured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {event.featured ? 'Featured' : 'Not Featured'}
                        </button>
                        <button onClick={() => setEditingEvent(event)} className="text-[#0F2137] hover:text-[#D90429] p-1"><Edit2 size={16} /></button>
                        <button onClick={() => handleDelete('events', event.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Transactions Tab */}
            {activeTab === "transactions" && (
              <div>
                <div className="p-4 bg-gray-50 font-medium text-sm text-[#495057]">Payment Transactions ({data.transactions.length})</div>
                <div className="divide-y">
                  {data.transactions.length === 0 ? <p className="p-6 text-center text-[#495057]">No transactions yet. Payments will appear here after Stripe checkout.</p> :
                    data.transactions.map((tx, i) => (
                      <div key={tx.id || tx.session_id || i} className="p-4 flex items-center gap-4 hover:bg-gray-50 text-sm" data-testid={`admin-tx-${tx.session_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#0F2137] truncate">{tx.metadata?.items || "Order"}</p>
                          <p className="text-xs text-[#495057] truncate">Session: {tx.session_id}</p>
                          {tx.file_ids && tx.file_ids.length > 0 && (
                            <p className="text-xs text-[#D90429] mt-0.5">{tx.file_ids.length} file(s) attached</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-[#0F2137]">${tx.amount?.toFixed(2)} <span className="uppercase text-xs text-[#495057]">{tx.currency}</span></p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                          tx.payment_status === 'paid' ? 'bg-green-100 text-green-700' :
                          tx.payment_status === 'initiated' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-500'
                        }`}>
                          {tx.payment_status}
                        </span>
                        <p className="text-xs text-[#495057] whitespace-nowrap">{tx.created_at ? new Date(tx.created_at).toLocaleDateString() : ''}</p>
                        <button onClick={() => handleDelete('transactions', tx.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Reviews Tab */}
            {activeTab === "reviews" && (
              <div className="divide-y">
                <div className="p-4 bg-gray-50 font-medium text-sm text-[#495057] grid grid-cols-12 gap-4">
                  <span className="col-span-3">Name</span><span className="col-span-4">Review</span><span className="col-span-1">Rating</span><span className="col-span-2">Featured</span><span className="col-span-2">Actions</span>
                </div>
                {data.reviews.length === 0 ? <p className="p-6 text-center text-[#495057]">No reviews found.</p> :
                  data.reviews.map(review => (
                    <div key={review.id} className="p-4 grid grid-cols-12 gap-4 items-center text-sm hover:bg-gray-50" data-testid={`admin-review-${review.id}`}>
                      <div className="col-span-3"><p className="font-medium text-[#0F2137]">{review.name}</p>{review.organization && <p className="text-xs text-[#495057]">{review.organization}</p>}</div>
                      <p className="col-span-4 text-[#495057] truncate">{review.text}</p>
                      <div className="col-span-1 flex gap-0.5">{[...Array(review.rating)].map((_, i) => <Star key={`star-${review.id}-${i}`} size={14} className="text-amber-400 fill-amber-400" />)}</div>
                      <div className="col-span-2"><button onClick={() => handleToggleFeatured('reviews', review.id, review.featured)} className={`text-xs px-3 py-1 rounded-full ${review.featured ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{review.featured ? 'Featured' : 'Not Featured'}</button></div>
                      <div className="col-span-2"><button onClick={() => handleDelete('reviews', review.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button></div>
                    </div>
                  ))}
              </div>
            )}

            {/* Contacts Tab */}
            {activeTab === "contacts" && (
              <div className="divide-y">
                <div className="p-4 bg-gray-50 font-medium text-sm text-[#495057] grid grid-cols-12 gap-4">
                  <span className="col-span-2">Name</span><span className="col-span-2">Email</span><span className="col-span-2">Type</span><span className="col-span-3">Message</span><span className="col-span-2">Date</span><span className="col-span-1">Actions</span>
                </div>
                {data.contacts.length === 0 ? <p className="p-6 text-center text-[#495057]">No contact submissions yet.</p> :
                  data.contacts.map((c, i) => (
                    <div key={c.id || i} className={`p-4 grid grid-cols-12 gap-4 items-center text-sm hover:bg-gray-50 ${c.is_spam ? "bg-red-50/60" : ""}`}>
                      <p className="col-span-2 font-medium text-[#0F2137] truncate" title={c.is_spam ? `Flagged: ${(c.spam_reasons || []).join(", ")}` : undefined}>
                        {c.is_spam && <span className="mr-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 align-middle">SPAM</span>}
                        {c.name}
                      </p>
                      <p className="col-span-2 text-[#495057] truncate">{c.email}</p>
                      <p className="col-span-2"><span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">{c.form_type}</span></p>
                      <p className="col-span-3 text-[#495057] truncate">{c.message}</p>
                      <p className="col-span-2 text-xs text-[#495057]">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</p>
                      <div className="col-span-1"><button onClick={() => handleDelete('contacts', c.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button></div>
                    </div>
                  ))}
              </div>
            )}

            {/* Callbacks Tab */}
            {activeTab === "callbacks" && (
              <div className="divide-y">
                <div className="p-4 bg-gray-50 font-medium text-sm text-[#495057] grid grid-cols-12 gap-4">
                  <span className="col-span-2">Name</span><span className="col-span-2">Phone</span><span className="col-span-2">Email</span><span className="col-span-2">Service</span><span className="col-span-2">Pref. Time</span><span className="col-span-1">Date</span><span className="col-span-1"></span>
                </div>
                {data.callbacks.length === 0 ? <p className="p-6 text-center text-[#495057]">No callback requests yet.</p> :
                  data.callbacks.map((cb, i) => (
                    <div key={cb.id || i} className={`p-4 grid grid-cols-12 gap-4 items-center text-sm hover:bg-gray-50 ${cb.is_spam ? "bg-red-50/60" : ""}`}>
                      <p className="col-span-2 font-medium text-[#0F2137] truncate" title={cb.is_spam ? `Flagged: ${(cb.spam_reasons || []).join(", ")}` : undefined}>
                        {cb.is_spam && <span className="mr-1 text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 align-middle">SPAM</span>}
                        {cb.name}
                      </p>
                      <p className="col-span-2 text-[#495057]">{cb.phone}</p>
                      <p className="col-span-2 text-[#495057] truncate">{cb.email}</p>
                      <p className="col-span-2"><span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700 capitalize">{cb.service}</span></p>
                      <p className="col-span-2 text-[#495057]">{cb.preferred_time || 'Any'}</p>
                      <p className="col-span-1 text-xs text-[#495057]">{cb.created_at ? new Date(cb.created_at).toLocaleDateString() : ''}</p>
                      <div className="col-span-1"><button onClick={() => handleDelete('callbacks', cb.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button></div>
                    </div>
                  ))}
              </div>
            )}

            {/* Uploads Tab */}
            {activeTab === "uploads" && (
              <div className="divide-y">
                <div className="p-4 bg-gray-50 font-medium text-sm text-[#495057] grid grid-cols-12 gap-4">
                  <span className="col-span-3">Filename</span><span className="col-span-2">Type</span><span className="col-span-1">Size</span><span className="col-span-2">Status</span><span className="col-span-2">Date</span><span className="col-span-2">Actions</span>
                </div>
                {data.uploads.length === 0 ? <p className="p-6 text-center text-[#495057]">No uploaded files yet.</p> :
                  data.uploads.map((file, i) => (
                    <div key={file.id || i} className="p-4 grid grid-cols-12 gap-4 items-center text-sm hover:bg-gray-50" data-testid={`admin-upload-${file.id}`}>
                      <p className="col-span-3 font-medium text-[#0F2137] truncate">{file.original_filename}</p>
                      <p className="col-span-2"><span className="text-xs px-2 py-1 rounded-full bg-blue-100 text-blue-700">{file.content_type?.split('/')[1]?.toUpperCase() || 'FILE'}</span></p>
                      <p className="col-span-1 text-[#495057]">{file.size ? `${(file.size / 1024).toFixed(1)} KB` : 'N/A'}</p>
                      <div className="col-span-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${file.activated ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {file.activated ? 'Paid' : 'Pending'}
                        </span>
                        {file.session_id && <p className="text-[10px] text-[#495057] mt-0.5 truncate" title={file.session_id}>Order: {file.session_id.slice(0, 12)}...</p>}
                      </div>
                      <p className="col-span-2 text-xs text-[#495057]">{file.created_at ? new Date(file.created_at).toLocaleDateString() : ''}</p>
                      <div className="col-span-2 flex items-center gap-2">
                        <a href={`${API}/files/${file.id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#D90429] hover:text-[#B00322] text-sm font-medium" data-testid={`download-file-${file.id}`}>
                          <Download size={16} /> View
                        </a>
                        <button onClick={() => handleDelete('uploads', file.id)} className="text-red-500 hover:text-red-700 p-1"><Trash2 size={16} /></button>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Site Media Tab */}
            {activeTab === "site_images" && (
              <div>
                <div className="p-4 bg-gray-50 text-sm text-[#495057]">
                  Replace any image or video on the public site. Upload a new file or paste a URL, then click Save. Use Reset to restore the original. Changes appear on the site after the page is refreshed.
                </div>
                <div className="px-4 py-3 bg-[#0F2137] text-white text-sm font-semibold">Page Embeds <span className="font-normal text-white/70">(ticket widgets)</span></div>
                <div className="divide-y">
                  {SITE_EMBED_SLOTS.map(slot => (
                    <SiteEmbedRow key={slot.key} slot={slot} currentUrl={getSiteImage(slot.key)} onSave={handleSaveSiteImage} />
                  ))}
                </div>
                <div className="px-4 py-3 bg-[#0F2137] text-white text-sm font-semibold">Page Images</div>
                <div className="divide-y">
                  {SITE_IMAGE_SLOTS.map(slot => (
                    <SiteImageRow key={slot.key} slot={slot} currentUrl={getSiteImage(slot.key)} onSave={handleSaveSiteImage}
                      positionValue={getSiteImage(`${slot.key}_pos`)} onSavePosition={(key, value) => handleSaveSiteImage(`${key}_pos`, value)} />
                  ))}
                </div>
                <div className="px-4 py-3 bg-[#0F2137] text-white text-sm font-semibold">Medal Gallery Videos <span className="font-normal text-white/70">(MP4, WebM, or MOV — max 60MB each. Add, edit, or remove any.)</span></div>
                <div className="divide-y">
                  {(data.medal_videos || []).map((video, index) => (
                    <MedalVideoRow key={video.id} video={video} onUpdate={handleUpdateMedalVideo} onDelete={(id) => handleDelete('medal-videos', id)}
                      onMoveUp={() => handleMoveMedalVideo(index, -1)} onMoveDown={() => handleMoveMedalVideo(index, 1)}
                      isFirst={index === 0} isLast={index === (data.medal_videos || []).length - 1} />
                  ))}
                </div>
                <AddMedalVideo onAdd={handleAddMedalVideo} />
              </div>
            )}
          </div>
        )}
      </div>

      {editingMerch && <EditMerchModal product={editingMerch} onClose={() => setEditingMerch(null)} onSave={handleSaveMerch} />}
      {(editingEvent || showNewEvent) && <EventFormModal event={editingEvent} onClose={() => { setEditingEvent(null); setShowNewEvent(false); }} onSave={handleSaveEvent} />}
      {showPasswordModal && <ChangePasswordModal onClose={() => setShowPasswordModal(false)} authHeader={authHeader} />}
    </div>
  );
}
